import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Drawer, ModalClose, Stack, Typography } from "@mui/joy";
import { ApiCategory, ApiCompetition, ApiLeague } from "griptonite-api-client";
import { HTTPError } from "griptonite-api-client/client";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  useCreateCategoryMutation,
  useDeleteCategoryMutation,
  useUpdateCategoryMutation,
  useUpdateCompetitionMutation,
  useUpdateLeagueMutation,
} from "@/hooks/competitions";
import { useCurrentVenueUuid } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import { ColorsFormInput } from "../forms/ColorsSelect";
import TextInput, { NumberInput } from "../forms/TextInput";
import { ToggleButtonSelectInput } from "../forms/ToggleButtonSelect";
import {
  CategoryFormSchema,
  CategoryFormValues,
  categoryFormValuesToPayload,
  categoryToCategoryFormValues,
} from "./forms";

const CompetitionCategoryDrawer = ({
  category,
  competition,
  league,
  open,
  onClose,
}: {
  category?: ApiCategory;
  competition?: ApiCompetition;
  league?: ApiLeague;
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <Drawer open={open} anchor="right" onClose={onClose}>
      <Stack sx={{ height: "100dvh" }}>
        <Stack direction="row" padding={1.5} alignItems="center">
          <Typography level="h3" flex={1}>
            {!category ? "Add category" : "Category details"}
          </Typography>
          <ModalClose sx={{ position: "static" }} />
        </Stack>
        <CategoryForm
          category={category}
          competition={competition}
          league={league}
          onClose={onClose}
        />
      </Stack>
    </Drawer>
  );
};

const CategoryForm = ({
  category,
  competition,
  league,
  onClose,
}: {
  category?: ApiCategory;
  competition?: ApiCompetition;
  league?: ApiLeague;
  onClose: () => void;
}) => {
  const defaultValues = useMemo(() => categoryToCategoryFormValues(category), [category]);
  const methods = useForm({
    defaultValues,
    resolver: zodResolver(CategoryFormSchema),
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const venueUuid = useCurrentVenueUuid();

  const createMutation = useCreateCategoryMutation({
    competitionUuid: competition?.uuid,
    leagueUuid: league?.uuid,
    venueUuid,
  });
  const updateMutation = useUpdateCategoryMutation(category?.uuid, {
    competitionUuid: competition?.uuid,
    leagueUuid: league?.uuid,
    venueUuid,
  });
  const deleteMutation = useDeleteCategoryMutation(category?.uuid, {
    competitionUuid: competition?.uuid,
    leagueUuid: league?.uuid,
    venueUuid,
  });

  const updateCompMutation = useUpdateCompetitionMutation(
    competition?.uuid,
    venueUuid,
    competition?.league?.uuid,
  );
  const updateLeagueMutation = useUpdateLeagueMutation(league?.uuid, venueUuid);

  const onSubmit = async (data: CategoryFormValues) => {
    if (!venueUuid) {
      throw new Error("Missing venue");
    }
    if (!competition && !league) {
      throw new Error("Missing competition/league");
    }
    if (category) {
      try {
        await updateMutation.mutateAsync(categoryFormValuesToPayload(data, venueUuid));
        onClose();
      } catch (error) {
        handleFormError(error as Error, methods);
        return;
      }
    } else {
      let newCategory: ApiCategory;
      try {
        newCategory = await createMutation.mutateAsync(
          categoryFormValuesToPayload(data, venueUuid),
        );
      } catch (error) {
        handleFormError(error as Error, methods);
        return;
      }
      if (competition) {
        await updateCompMutation.mutateAsync({
          categories: [...competition.stages[0].categories.map((c) => c.uuid), newCategory.uuid],
        });
      } else if (league) {
        await updateLeagueMutation.mutateAsync({
          categories: [...league.categories.map((c) => c.uuid), newCategory.uuid],
        });
      }
      onClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack
        sx={{ flex: 1, overflowY: "auto", padding: 1.5 }}
        component="form"
        autoComplete="off"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <Stack gap={1.5} flex={1}>
          <FormErrorAlert />
          <CategoryFormFields />
        </Stack>
        <Stack spacing={1} marginTop={2}>
          <Button
            type="submit"
            color="primary"
            loading={methods.formState.isSubmitting}
            loadingPosition="start"
          >
            Save
          </Button>
          {category ? (
            <Button
              color="danger"
              onClick={() => {
                deleteMutation.mutate(undefined, {
                  onSuccess: () => onClose(),
                  onError: (error) => {
                    if (
                      error instanceof HTTPError &&
                      error.jsonData.type === "CategoryHasEntries"
                    ) {
                      alert(
                        "This category has been entered by competitors. Please reassign these entries to a different category before deleting this category.",
                      );
                    } else {
                      alert(
                        "An unexpected error occurred when deleting the category. Please try again or contact support",
                      );
                    }
                  },
                });
              }}
              loading={deleteMutation.isPending}
              loadingPosition="start"
            >
              Delete
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </FormProvider>
  );
};

const CategoryFormFields = () => {
  return (
    <>
      <TextInput name="name" label="Name" />
      <ToggleButtonSelectInput
        name="gender"
        label="Genders"
        options={[
          {
            name: "Everyone",
            value: null,
          },
          {
            name: "Male",
            value: "MALE",
          },
          {
            name: "Female",
            value: "FEMALE",
          },
        ]}
      />
      <Box>
        <Stack direction="row" gap={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <NumberInput name="minAge" label="Min age" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <NumberInput name="maxAge" label="Max age" />
          </Box>
        </Stack>
        <Typography level="body-sm" textColor="neutral.600">
          Leave empty for no age restrictions.
        </Typography>
      </Box>
      <ToggleButtonSelectInput
        name="abilityCategories"
        label="Ability categories"
        allName="Everyone"
        options={[
          {
            name: "Beginner",
            value: "BEGINNER",
          },
          {
            name: "Novice",
            value: "NOVICE",
          },
          {
            name: "Intermediate",
            value: "INTERMEDIATE",
          },
          {
            name: "Advanced",
            value: "ADVANCED",
          },
          {
            name: "Professional",
            value: "PROFESSIONAL",
          },
          {
            name: "Elite",
            value: "ELITE",
          },
        ]}
      />
      <ColorsFormInput name="color" label="Color" multiple={false} />
    </>
  );
};

export default CompetitionCategoryDrawer;
